import {
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import store from 'store';

type JobType = {
  id: string;
  rivalId: number;
  // When true, CreateRivalNotifier still polls the job to know when it
  // completes but suppresses the "started"/"complete" toasts. Callers
  // that render their own progress UI (e.g. the New Board modal's
  // ProgressPanel) opt in so the two feedbacks don't overlap.
  silent?: boolean;
};

export type GlobalRivals = {
  jobs: JobType[];
};

const initialState: GlobalRivals = {
  jobs: [],
};

const globalRivals = createSlice({
  name: 'globalRivals',
  initialState,
  reducers: {
    addJob(state, action: PayloadAction<JobType>) {
      state.jobs.push(action.payload);
    },
    removeJob(state, action: PayloadAction<{ id: string }>) {
      state.jobs = state.jobs.filter((job) => job.id !== action.payload.id);
    },
  },
});

store.addModel({
  name: 'globalRivals',
  state: globalRivals.getInitialState(),
  reducers: {},
  baseReducer: globalRivals.reducer,
});

const selectSelf = createSelector(
  [(state: { globalRivals: GlobalRivals }) => state.globalRivals],
  (globalRivals) => globalRivals,
);

const selectGlobalRivalJobs = createSelector(
  [selectSelf],
  (globalRivals) => globalRivals.jobs,
);

export const useGlobalRivalJobs = () => useSelector(selectGlobalRivalJobs);

export const addJob = (job: JobType) =>
  store.dispatch(globalRivals.actions.addJob(job));

export const removeJob = (jobId: string) =>
  store.dispatch(globalRivals.actions.removeJob({ id: jobId }));
